import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LangflowProvider } from '../../context/LangflowContext';
import LangflowChatModal from '../LangflowChatModal';
import FloatingChatButton from '../FloatingChatButton';
import WellnessBuddyLaunchModal from '../WellnessBuddyLaunchModal';
import track from 'lib/track';

/**
 * FloatingChat - Complete floating chat solution with button and modal
 * Combines FloatingChatButton and LangflowChatModal for easy integration
 */
const FloatingChat = ({
  // Langflow configuration
  authToken,
  flowId,
  userId,

  // Chat modal props
  title = 'VirtualMentor',
  onMessageSent,
  onError,
  onScore,

  // Suggestions props
  suggestions = [],
  showSuggestionsWhenEmpty = true,
  suggestionsTitle = 'Suggested prompts',
  maxSuggestions = null,

  // Floating button props
  buttonSize = 'medium',
  buttonPosition = 'bottom-right',
  buttonColor = '#0b93f6',
  buttonClassName = '',

  // State management
  defaultOpen = false,
  onToggle,

  // Advanced features
  showUnreadCount = false,
  disabled = false,
  autoOpen = false,
  autoOpenDelay = 3000,
  disableLaunchModal = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [unreadCount, setUnreadCount] = useState(0);

  // Auto-open functionality
  useEffect(() => {
    if (autoOpen && !defaultOpen) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, autoOpenDelay);

      return () => clearTimeout(timer);
    }
  }, [autoOpen, autoOpenDelay, defaultOpen]);

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    // Clear unread count when opening chat
    if (newIsOpen) {
      setUnreadCount(0);
    }

    // Call external toggle handler if provided
    if (onToggle) {
      onToggle(newIsOpen);
    }
    track(`Modal ${newIsOpen ? 'Opened' : 'Closed'}`, { testId: 'wellness-buddy-modal' });
  };

  const handleMessageSent = result => {
    if (onMessageSent) {
      onMessageSent(result);
    }
  };

  const handleError = error => {
    if (onError) {
      onError(error);
    }
  };

  const handleScore = scoreData => {
    if (onScore) {
      onScore(scoreData);
    }
  };

  // Mock function to simulate new messages (you can connect this to your actual message system)
  const handleNewMessage = () => {
    if (!isOpen && showUnreadCount) {
      setUnreadCount(prev => prev + 1);
    }
  };

  return (
    <LangflowProvider authToken={authToken} flowId={flowId} userId={userId}>
      <WellnessBuddyLaunchModal onStart={handleToggle} disabled={disableLaunchModal} />
      <FloatingChatButton
        isOpen={isOpen}
        onClick={handleToggle}
        unreadCount={showUnreadCount ? unreadCount : 0}
        size={buttonSize}
        position={buttonPosition}
        color={buttonColor}
        className={buttonClassName}
        disabled={disabled}
      />

      <LangflowChatModal
        isOpen={isOpen}
        toggle={handleToggle}
        title={title}
        onMessageSent={handleMessageSent}
        onError={handleError}
        onScore={handleScore}
        suggestions={suggestions}
        showSuggestionsWhenEmpty={showSuggestionsWhenEmpty}
        suggestionsTitle={suggestionsTitle}
        maxSuggestions={maxSuggestions}
      />
    </LangflowProvider>
  );
};

FloatingChat.propTypes = {
  // Langflow configuration
  authToken: PropTypes.string.isRequired,
  flowId: PropTypes.string.isRequired,

  // Chat modal props
  title: PropTypes.string,
  onMessageSent: PropTypes.func,
  onError: PropTypes.func,
  onScore: PropTypes.func,

  // Suggestions props
  suggestions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  showSuggestionsWhenEmpty: PropTypes.bool,
  suggestionsTitle: PropTypes.string,
  maxSuggestions: PropTypes.number,

  // Floating button props
  buttonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  buttonPosition: PropTypes.oneOf(['bottom-right', 'bottom-left', 'top-right', 'top-left']),
  buttonColor: PropTypes.string,
  buttonClassName: PropTypes.string,

  // State management
  defaultOpen: PropTypes.bool,
  onToggle: PropTypes.func,

  // Advanced features
  showUnreadCount: PropTypes.bool,
  disabled: PropTypes.bool,
  autoOpen: PropTypes.bool,
  autoOpenDelay: PropTypes.number
};

export default FloatingChat;
